/* eslint-disable @typescript-eslint/no-use-before-define */
import { LinkButton } from '@euler/app/components/LinkButton';
import { StatusLabel } from '@euler/app/components/StatusLabel';
import { Typography } from '@euler/app/components/typography';
import { MemberChatButton } from '@euler/app/flows/chat/components/MemberChatButton';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { constructionJobStatusToTaskStatus } from '@euler/app/flows/task/functions';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { ConstructionJobTechnicianBottomSheetModal } from '@euler/app/flows/task/pages/construction/components/ConstructionJobTechnicianBottomSheetModal';
import { TaskCardTitle } from '@euler/app/flows/task/pages/detail/components/TaskCardTitle';
import { TimeElapsedLabel } from '@euler/app/flows/task/pages/detail/components/TimeElapsedLabel';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMember } from '@euler/model/entity';
import {
  CommonTaskStatus,
  InspectionTaskJobStatus,
  InspectionTaskStatus,
} from '@euler/model/enum';
import {
  Assignee,
  Construction,
  ConstructionJob,
  TaskProductManifestItem,
} from '@euler/model/task-detail';
import { idEquals, showAlert } from '@euler/utils';
import { timeUtilNow } from '@euler/utils/formatters';
import { useLocalObservable, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTheme } from 'styled-components';
import { TaskDetailSection } from './TaskDetailSection';

export const WiredConstructionInfo = memo(() => {
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const appLoading = useAppLoading();

  const { detail, taskManager } = useTaskContext();
  const extraInfo = useTaskExtraInfo();
  const { construction } = detail;

  const [isTechModalVisible, setIsTechModalVisible] = useState(false);
  const continueBeginConstructionJobRef = useRef<ConstructionJob>();
  const productManifestItems = useLocalObservable(
    taskManager.productManifestManager.manifest$,
    [],
    [],
  );

  const onSchedule = useCallback(() => {
    navigation.navigate('_scheduleConstructionJobs', {});
  }, [navigation]);

  const beginConstructionJob = usePersistFn(
    async (job: ConstructionJob, assignee: Assignee) => {
      try {
        appLoading.show();
        await taskManager.sync();
        await taskManager.constructionManager.beginConstructionJob(
          job.id,
          assignee.id,
          assignee.name,
        );
        navigation.navigate('_constructionJob', { jobId: job.id });
        appLoading.hide();
      } catch (e) {
        appLoading.hide();
        void showAlert('开始施工失败', (e as Error).message);
      }
    },
  );

  const onJobPress = usePersistFn(async (job: ConstructionJob) => {
    if (!job.technicianId || !job.technicianName) {
      continueBeginConstructionJobRef.current = job;
      setIsTechModalVisible(true);
      return;
    }
    await beginConstructionJob(job, {
      id: job.technicianId,
      name: job.technicianName,
    });
  });

  const viewReport = useViewReportWithTaskContext();

  const onHeaderRightPress = usePersistFn(() => {
    viewReport('constructionReport', {
      onFinish: async () => {
        await taskManager.constructionManager.finish();
      },
    });
  });

  const onTechnicianSelected = usePersistFn(async (technician: OrgMember) => {
    setIsTechModalVisible(false);
    const job = continueBeginConstructionJobRef.current;
    if (!job) return;
    continueBeginConstructionJobRef.current = undefined;
    await beginConstructionJob(job, {
      id: technician.id,
      name: technician.name,
    });
  });

  const onTechnicianModalDismiss = useCallback(() => {
    setIsTechModalVisible(false);
    continueBeginConstructionJobRef.current = undefined;
  }, []);

  if (
    detail.basicInfo.status === InspectionTaskStatus.Finished &&
    construction.status !== CommonTaskStatus.Finished
  ) {
    return null;
  }

  return (
    <>
      <TaskDetailSection
        title={
          <TaskCardTitle
            text="维保施工"
            status={construction.status}
            shared={extraInfo?.reportShareSummary?.construction}
            viewed={extraInfo?.reportViewSummary?.construction}
          />
        }
        headerRight={
          construction.status === CommonTaskStatus.Finished
            ? '查看报告'
            : construction.jobs.length > 0 &&
              construction.jobs.some(
                x => x.status !== InspectionTaskJobStatus.Pending,
              )
            ? '预览报告'
            : undefined
        }
        headerRightArrow
        onHeaderRightPress={onHeaderRightPress}
        bodyStyle={{
          paddingLeft: 0,
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <Content
          detail={construction}
          productManifestItems={productManifestItems}
          canSchedule={
            detail.basicInfo.status !== InspectionTaskStatus.Finished &&
            detail.basicInfo.status !== InspectionTaskStatus.Cancelled &&
            construction.status !== CommonTaskStatus.Finished
          }
          onSchedule={onSchedule}
          onJobPress={onJobPress}
        />
      </TaskDetailSection>
      <ConstructionJobTechnicianBottomSheetModal
        visible={isTechModalVisible}
        onSelect={onTechnicianSelected}
        onDismiss={onTechnicianModalDismiss}
      />
    </>
  );
});

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        subTitle: {
          fontSize: rpx(10),
          borderWidth: 0.5,
          borderColor: '#ddd',
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: rpx(3),
          paddingRight: rpx(3),
          lineHeight: rpx(14),
          borderRadius: rpx(3),
          top: rpx(-2),
          color: '#777',
        },
      }),
    [rpx],
  );
};

export const Content = memo(
  ({
    detail,
    canSchedule,
    productManifestItems,
    onSchedule,
    onJobPress,
  }: {
    detail: Construction;
    productManifestItems: TaskProductManifestItem[];
    canSchedule?: boolean;
    onSchedule?: () => void;
    onJobPress?: (job: ConstructionJob) => void;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const styles = useStyles();

    return (
      <TableView>
        {detail.jobs.map(job => {
          const totalProductCount = productManifestItems.filter(
            x => x.subject === job.name,
          ).length;
          const confirmedProductCount = productManifestItems.filter(
            x =>
              x.subject === job.name &&
              job.context?.productConfirmation?.items.some(k =>
                idEquals(k.productId, x.product.id),
              ),
          ).length;
          const unconfirmedProductCount =
            totalProductCount - confirmedProductCount;

          let productDesc: string | undefined = undefined;
          const productDescBgColor =
            totalProductCount > 0
              ? unconfirmedProductCount > 0
                ? StatusColors.Warn
                : StatusColors.Success
              : undefined;
          if (totalProductCount > 0) {
            productDesc = [
              `${totalProductCount}个产品推荐`,
              unconfirmedProductCount > 0
                ? `${unconfirmedProductCount}个待确认`
                : '已全部确认',
            ].join(', ');
          }

          return (
            <TableView.Item
              key={job.id}
              title={job.name}
              subTitle={job.context?.category}
              titleStyle={{ marginBottom: rpx(5) }}
              titleTextStyle={{ fontSize: rpx(16) }}
              subTitleTextStyle={styles.subTitle}
              onPress={() => onJobPress?.(job)}
              note={
                <>
                  {productDesc != null ? (
                    <HStack
                      justifyContent="flex-start"
                      alignItems="center"
                      css={`
                        margin-bottom: ${rpx(4)}px;
                      `}
                    >
                      <View
                        css={`
                          width: ${rpx(4.5)}px;
                          height: ${rpx(4.5)}px;
                          border-radius: ${rpx(4)}px;
                          background-color: ${productDescBgColor};
                          margin-right: ${rpx(4)}px;
                          top: ${rpx(1)}px;
                        `}
                      />
                      <Typography.Label
                        medium
                        color={productDescBgColor}
                        size={rpx(11)}
                        lineHeight={rpx(18)}
                      >
                        {productDesc}
                      </Typography.Label>
                    </HStack>
                  ) : null}
                  <View
                    css={`
                      flex-direction: row;
                      align-items: center;
                    `}
                  >
                    <Typography.Label
                      light
                      size={rpx(11)}
                      color={t => t.components.table.item.noteColor}
                    >
                      {`施工技师: `}
                    </Typography.Label>
                    <Typography.Label
                      light
                      size={rpx(11)}
                      color={t =>
                        job.technicianId == null
                          ? StatusColors.Danger
                          : t.components.table.item.noteColor
                      }
                    >
                      {job.technicianName ?? '[未派工]'}
                    </Typography.Label>
                    {job.technicianId != null ? (
                      <MemberChatButton
                        orgMemberId={job.technicianId}
                        size={rpx(14)}
                        style={{ marginLeft: rpx(4) }}
                      />
                    ) : null}
                    <Typography.Label
                      size={rpx(12)}
                      color={p => p.components.table.item.noteColor}
                      opacity={0.5}
                      ml={rpx(5)}
                      light
                    >
                      {`${timeUtilNow(job.createdAt)}`}
                    </Typography.Label>
                  </View>
                </>
              }
              noteStyle={{ fontSize: rpx(12) }}
              iconStyle={{ marginRight: rpx(10) }}
              detailIcon="disclosure"
              css={`
                padding: ${rpx(15)}px 0;
                min-height: ${rpx(74)}px;
              `}
              detail={
                <View
                  css={`
                    align-items: flex-end;
                    justify-content: center;
                    margin-left: ${rpx(15)}px;
                  `}
                >
                  <StatusLabel
                    status={constructionJobStatusToTaskStatus(job.status)}
                    continuable
                    continueText="继续施工"
                    css={`
                      margin-left: ${rpx(5)}px;
                    `}
                  />
                  <TimeElapsedLabel
                    flow={{
                      createdAt: job.createdAt,
                      finishedAt: job.finishedAt,
                      status:
                        job.status === InspectionTaskJobStatus.Finished
                          ? 'finished'
                          : job.status === InspectionTaskJobStatus.InProgress
                          ? 'inprogress'
                          : 'pending',
                    }}
                  />
                </View>
              }
            />
          );
        })}
        {detail.jobs.length === 0 && canSchedule ? (
          <TableView.Item onPress={onSchedule}>
            <Typography.Label
              light
              color={p => p.form.label.color}
              size={rpx(14)}
            >
              施工未开始，点击添加施工任务
            </Typography.Label>
          </TableView.Item>
        ) : null}
        {canSchedule ? (
          <TableView.Item
            onPress={onSchedule}
            contentContainerStyle={{
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <LinkButton
              text="添加施工任务"
              onPress={onSchedule}
              textStyle={{
                lineHeight: rpx(18),
              }}
            />
            <Icon
              name="add-circle-outline"
              size={rpx(18)}
              color={theme.link}
              css={`
                margin-left: ${rpx(5)}px;
              `}
            />
          </TableView.Item>
        ) : null}
      </TableView>
    );
  },
);
